import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import { CompanyEntity } from './company.entity';
import { ItemEntity } from './item.entity';

/**
 * Why a recommendation was made — drives the plain-English reason a rep reads
 * and lets the UI badge/group recommendations by their source.
 *   COPURCHASE       — item-based collaborative filtering over the account's own
 *                      purchase history ("accounts that buy X also buy this").
 *   SIMILAR_ACCOUNTS — (reserved, fast-follow) kNN over lookalike accounts.
 *   POPULAR_FALLBACK — cold-start: popular among accounts in the same business
 *                      type / area when the account has little/no history.
 */
export type RecommendationReasonCode =
	| 'COPURCHASE'
	| 'SIMILAR_ACCOUNTS'
	| 'POPULAR_FALLBACK';

/**
 * Per-(client, item) account recommendation, precomputed nightly by the
 * client-item-recommendation queue (PD-14016). The Recommended surfaces (Shop,
 * Item Insights) read straight from this table so they're instant, instead of
 * running the live 5-factor CTE per request.
 *
 * Recommended is for DISCOVERY — items already in the account's recent
 * repertoire are excluded here on purpose; repurchasing is handled by Reorder.
 */
@Index('idx_client_item_recommendation_client_rank', ['fkClientId', 'rank'])
@Index('uq_client_item_recommendation_pair', ['fkClientId', 'fkItemId'], {
	unique: true,
})
@Entity('client_item_recommendation', {}, { auditable: false })
export class ClientItemRecommendationEntity extends BaseEntity {
	@Column('int', { name: 'fk_client__id', unsigned: true })
	public fkClientId!: number;

	@Column('int', { name: 'fk_item__id', unsigned: true })
	public fkItemId!: number;

	@Column('int', { name: 'fk_company__id', unsigned: true })
	public fkCompanyId!: number;

	/** 1..N position within this client's recommendation list (1 = best). */
	@Column('int', { name: 'rank', unsigned: true })
	public rank!: number;

	/** Affinity score: Σ over the account's purchases X of cosine(X,Y)·w(X). */
	@Column('decimal', { name: 'score', precision: 18, scale: 8, default: 0 })
	public score!: string;

	@Column('varchar', { name: 'reasonCode', length: 32 })
	public reasonCode!: RecommendationReasonCode;

	/** Plain-English reason, precomputed at batch time so the UI renders it as-is. */
	@Column('varchar', { name: 'reasonText', length: 255 })
	public reasonText!: string;

	/**
	 * The item ids in the account's own history that drove this recommendation
	 * (the top contributing "anchor" purchases). Stored for explainability /
	 * drill-down. Null for POPULAR_FALLBACK rows (no account-specific anchors).
	 */
	@Column('json', { name: 'anchorItemIds', nullable: true })
	public anchorItemIds!: Array<number> | null;

	/** When this row was produced by the nightly batch. */
	@Column('datetime', { name: 'computedDate', precision: 0 })
	public computedDate!: Date;

	@ManyToOne(() => ClientEntity, { onDelete: 'CASCADE', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_client__id', referencedColumnName: 'id' }])
	public client!: ClientEntity;

	@ManyToOne(() => ItemEntity, { onDelete: 'CASCADE', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_item__id', referencedColumnName: 'id' }])
	public item!: ItemEntity;

	@ManyToOne(() => CompanyEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_company__id', referencedColumnName: 'id' }])
	public company!: CompanyEntity;
}
